// AppsDataTab — Admin view of every user's projects across all three apps
// (Simulation, Pattern & Blend, AV System Designer). Per-user expandable
// row so we don't fetch every user's apps-data eagerly (saves ~3× the
// network for typical admin sessions). Click a user → fetches once and
// caches the result for the remainder of the session.
//
// Lives inside /admin → Apps Data tab; mounted by AdminPanel.jsx.
import React, { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { Search, ChevronDown, ChevronRight, Box, Grid3x3, Workflow } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const fmtHours = (sec) => (((sec || 0) / 3600)).toFixed(1);

export default function AppsDataTab({ users, loading, token }) {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [appsCache, setAppsCache] = useState({}); // { [userId]: { simulation, tpg, avd, counts, user } }
  const [fetching, setFetching] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.signup_source || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggle = async (userId) => {
    if (expandedId === userId) { setExpandedId(null); return; }
    setExpandedId(userId);
    if (appsCache[userId]) return; // already loaded
    setFetching(userId);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/apps-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load apps data');
      setAppsCache((prev) => ({ ...prev, [userId]: data }));
    } catch (err) {
      toast.error(err.message || 'Could not load apps data');
      setExpandedId(null);
    } finally {
      setFetching(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-500">Loading users...</div>;
  }

  return (
    <div data-testid="apps-data-tab">
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users by name, email or signup source..."
          className="pl-10 bg-zinc-900 border-zinc-700 text-white"
          data-testid="apps-data-search"
        />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-300px)]">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm">No users found</div>
          ) : (
            filtered.map((u) => {
              const isOpen = expandedId === u.id;
              const cache = appsCache[u.id];
              return (
                <div key={u.id} className="border-b border-zinc-800/60" data-testid={`apps-data-row-${u.id}`}>
                  <button
                    type="button"
                    onClick={() => toggle(u.id)}
                    className="w-full px-3 sm:px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/40 text-left"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[11px]">
                      <span className="px-2 py-0.5 rounded bg-violet-500/15 text-violet-300" title="Simulation hours">
                        <Box className="w-3 h-3 inline -mt-0.5 mr-1" />{fmtHours(u.usage_seconds_simulation)} h
                      </span>
                      <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300" title="Pattern & Blend hours">
                        <Grid3x3 className="w-3 h-3 inline -mt-0.5 mr-1" />{fmtHours(u.usage_seconds_tpg)} h
                      </span>
                      <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300" title="AV Designer hours">
                        <Workflow className="w-3 h-3 inline -mt-0.5 mr-1" />{fmtHours(u.usage_seconds_avd)} h
                      </span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="bg-zinc-950/60 px-3 sm:px-6 py-4 border-t border-zinc-800/60">
                      {fetching === u.id || !cache ? (
                        <div className="py-6 text-center text-zinc-500 text-sm">Loading projects...</div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <ProjectColumn
                            color="violet"
                            icon={<Box className="w-4 h-4" />}
                            title="Simulation"
                            items={cache.simulation}
                          />
                          <ProjectColumn
                            color="cyan"
                            icon={<Grid3x3 className="w-4 h-4" />}
                            title="Pattern & Blend"
                            items={cache.tpg}
                          />
                          <ProjectColumn
                            color="amber"
                            icon={<Workflow className="w-4 h-4" />}
                            title="AV System Designer"
                            items={cache.avd}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const COLOR_CLS = {
  violet: 'border-violet-500/30 text-violet-200',
  cyan:   'border-cyan-500/30 text-cyan-200',
  amber:  'border-amber-500/30 text-amber-200',
};

function ProjectColumn({ color, icon, title, items }) {
  const bordered = COLOR_CLS[color] || COLOR_CLS.violet;
  return (
    <div className={`rounded-lg border ${bordered.split(' ')[0]} bg-zinc-900/40 p-3`} data-testid={`apps-data-col-${color}`}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-3 ${bordered.split(' ')[1]}`}>
        {icon}
        <span className="font-semibold">{title}</span>
        <span className="ml-auto text-zinc-500 normal-case tracking-normal">{items?.length || 0}</span>
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-zinc-600 italic py-4 text-center">No projects yet</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((p) => (
            <li key={p.id} className="text-xs flex items-start justify-between gap-2 bg-zinc-950/40 rounded px-2 py-1.5">
              <span className="text-zinc-200 truncate" title={p.name}>{p.name || '(untitled)'}</span>
              <span className="text-[10px] text-zinc-500 shrink-0">{fmtDate(p.updated_at || p.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
